import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { FloatingParticles, AnimatedOrbs, GridPattern } from './BackgroundEffects';

export default function HeroSection() {
  const words = ['sell.', 'wait.', 'ask.'];
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((i) => (i + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="relative min-h-[70vh] sm:min-h-[85vh] flex flex-col justify-center pt-24 sm:pt-28 pb-12 sm:pb-16 overflow-hidden">
      <AnimatedOrbs />
      <GridPattern />
      <FloatingParticles />

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Left - Text */}
          <div className="flex-1 max-w-xl text-center lg:text-left">
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-[10px] sm:text-[11px] uppercase tracking-[0.1em] font-semibold text-white/70 px-3 sm:px-4 py-1.5 rounded-full"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              enaibl by AIBL
            </motion.span>

            {/* Rotating Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-[26px] sm:text-[40px] lg:text-[52px] font-bold text-white leading-[1.15] sm:leading-[1.12] tracking-[-0.02em] mt-5 sm:mt-6"
            >
              Your agent doesn't need to{' '}
              <span className="relative inline-block min-w-[70px] sm:min-w-[120px]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={wordIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="text-white"
                    style={{ textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.3)', textUnderlineOffset: '4px' }}
                  >
                    {words[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.h1>

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-[13px] sm:text-[16px] text-white/55 leading-[1.6] sm:leading-[1.65] mt-4 sm:mt-6 max-w-[520px] mx-auto lg:mx-0"
            >
              The first lending protocol built for AI agents. Borrow ETH
              against token holdings via a single API call. No selling,
              no deadlines, no human intervention.
            </motion.p>

            {/* Stat Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 mt-6 sm:mt-8"
            >
              {[
                { value: 'Up to 55%', label: 'LTV' },
                { value: '1% / day', label: 'interest' },
                { value: 'One API call', label: '' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-[10px]"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="text-[12px] sm:text-[15px] font-semibold text-white">{stat.value}</div>
                  {stat.label && <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-white/40">{stat.label}</div>}
                </div>
              ))}
            </motion.div>

            {/* CTAs - API Docs primary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3 mt-6 sm:mt-8"
            >
              <motion.button 
                className="bg-white text-black font-semibold text-[14px] px-7 py-3 rounded-full flex items-center justify-center gap-2 w-full sm:w-auto"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="view-api-docs-btn"
              >
                View API Docs <ArrowRight size={14} />
              </motion.button>
              <motion.button 
                className="font-medium text-[14px] px-7 py-3 rounded-full text-white/80 w-full sm:w-auto"
                style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                whileHover={{ borderColor: 'rgba(255,255,255,0.3)' }}
                data-testid="launch-dashboard-btn"
              >
                Launch Dashboard
              </motion.button>
            </motion.div>
          </div>

          {/* Right - API Cards Animation - Hidden on mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="hidden lg:flex flex-1 justify-center relative"
          >
            <div className="relative w-[340px] h-[280px]">
              {/* Orbiting ring */}
              <motion.div
                className="absolute inset-0 rounded-full border border-white/[0.06]"
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              />
              <motion.div
                className="absolute inset-4 rounded-full border border-dashed border-white/[0.04]"
                animate={{ rotate: -360 }}
                transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
              />

              {/* Central glow */}
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }}
              />

              {/* API Card 1 - Agent Borrow */}
              <motion.div
                className="absolute top-4 right-0 w-[180px] p-4 rounded-xl"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(12px)',
                }}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Agent Borrow</div>
                <div className="text-[22px] font-bold text-white">3.52 ETH</div>
                <div className="text-[10px] font-mono text-white/30 mt-2">POST /v1/enaibl/borrow</div>
              </motion.div>

              {/* API Card 2 - Health Score */}
              <motion.div
                className="absolute bottom-8 left-0 w-[170px] p-4 rounded-xl"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(12px)',
                }}
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              >
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Health Score</div>
                <div className="text-[22px] font-bold text-green-400">1.82</div>
                <div className="text-[10px] font-mono text-white/30 mt-2">GET /v1/enaibl/health</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
